import {
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  Radio,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { UnifyDateInput } from '../../components/UnifyDateInput';
import { notify } from '@/lib/notify';
import {
  IconActivity,
  IconAlertCircle,
  IconBuilding,
  IconCalendar,
  IconCalendarEvent,
  IconCreditCard,
  IconDeviceFloppy,
  IconShield,
  IconSparkles,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import { IconArrowLeft } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMedplum } from '@medplum/react';
import type { Patient } from '@medplum/fhirtypes';
import { loadAiToolsConsentState } from '../../lib/aiToolsConsentClient';
import { UsStateSelect } from '@/components/UsStateSelect';
import { clientProfileFormSchema } from '@/schemas/patient.schema';
import { useClients } from '../../contexts/ClientsContext';
import { usePermissions } from '../../contexts/PermissionContext';
import {
  getVisibleClientProfileTabs,
  resolveClientProfileTab,
  type ClientProfileTabId,
} from '../../lib/clientProfileTabPermissions';
import { ProfileCheckboxSection } from '../../components/ProfileCheckboxSection';
import { formatDate } from '../../components/billing/helpers';
import { BillingTab } from './tabs/BillingTab';
import { ContactsTab } from './tabs/ContactsTab';
import { useClientProfileData } from './hooks/useClientProfileData';
import type { ClientProfileFormState } from './utils/clientProfileFhir';
import {
  patientToClientProfileForm,
  clientProfileFormToPatientPatch,
  getPatientOfficialName,
  getPatientPreferredName,
  getPatientInitials,
  getPatientEmail,
} from './utils/clientProfileFhir';
import {
  GENDER_IDENTITY_OPTIONS,
  LGBTQIA_OPTIONS,
  RELIGION_OPTIONS,
  RACE_ETHNICITY_OPTIONS,
  LANGUAGES_OPTIONS,
} from '../../lib/fhirValueSets/clientUserProfile';
import { getUserFacingErrorMessage, UserFacingError } from '@/lib/userFacingError';

// ---------------------------------------------------------------------------
// AI documentation opt-out card (provider-managed on behalf of client)
// ---------------------------------------------------------------------------

function ClientAiDocumentationStatusCard({
  patientId,
}: {
  patientId: string;
}): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState('Checking AI consent…');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadAiToolsConsentState(medplum, patientId)
      .then((state) => {
        if (cancelled) return;
        if (state.decision === 'active' && state.transcription === 'permit') {
          setSummary('Signed. Providers may use AI documentation, including meeting transcripts.');
        } else if (state.decision === 'active') {
          setSummary('Signed. Providers may use AI documentation. Session transcription is not allowed, so AI cannot use meeting transcripts.');
        } else if (state.decision === 'revoked') {
          setSummary('Revoked. AI documentation stays off until the client turns it back on in their settings.');
        } else {
          setSummary('Not signed. AI documentation stays off for every provider until the client signs the AI tools consent.');
        }
      })
      .catch(() => {
        if (!cancelled) setSummary('Could not load AI consent. AI documentation stays off until it can be confirmed.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [medplum, patientId]);

  return (
    <Card withBorder mt="md">
      <Stack gap="md">
        <Group gap="xs">
          <IconSparkles size={18} color="var(--mantine-color-violet-6)" />
          <Title order={4}>AI documentation assistance</Title>
        </Group>
        {loading ? <Loader size="sm" /> : (
          <Text size="sm">{summary}</Text>
        )}
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------


export function ClientProfilePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getClientById } = useClients();
  const { permissions } = usePermissions();
  const medplum = useMedplum();

  const visibleTabs = useMemo(
    () => getVisibleClientProfileTabs(permissions),
    [permissions],
  );

  const client = useMemo(() => (id ? getClientById(id) ?? null : null), [id, getClientById]);

  const patientId = id;
  const profileData = useClientProfileData(patientId);

  const tabQuery = searchParams.get('tab');
  const initialTab = resolveClientProfileTab(tabQuery, permissions);

  const [activeTab, setActiveTab] = useState<string | null>(initialTab);

  useEffect(() => {
    const resolved = resolveClientProfileTab(searchParams.get('tab'), permissions);
    setActiveTab(resolved);
    if (searchParams.get('tab') && searchParams.get('tab') !== resolved) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', resolved);
        return next;
      }, { replace: true });
    }
  }, [id, tabQuery, searchParams, permissions, setSearchParams]);

  const handleTabChange = (value: string | null) => {
    if (!value || !visibleTabs.includes(value as ClientProfileTabId)) return;
    setActiveTab(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    }, { replace: true });
  };

  // Profile form state — populated from Patient in live mode
  const [formState, setFormState] = useState<ClientProfileFormState>({
    preferredFirstName: '',
    preferredLastName: '',
    legalFirstName: '',
    legalLastName: '',
    dateOfBirth: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    genderIdentity: '',
    lgbtqia: [],
    religion: [],
    raceEthnicity: [],
    languages: [],
  });

  const [formInitialized, setFormInitialized] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Populate form from Patient data
  useEffect(() => {
    if (profileData.patient && !formInitialized) {
      setFormState(patientToClientProfileForm(profileData.patient));
      setFormInitialized(true);
      setHasChanges(false);
    }
  }, [profileData.patient, formInitialized]);

  // Reset form initialization on id change
  useEffect(() => {
    setFormInitialized(false);
  }, [id]);

  const updateField = <K extends keyof ClientProfileFormState>(
    key: K,
    value: ClientProfileFormState[K],
  ) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    if (!hasChanges) setHasChanges(true);
  };

  const handleSaveProfile = async () => {
    if (!profileData.patient) {
      notify({ type: 'error', title: 'Error', message: 'Patient data not loaded.' });
      return;
    }
    setSaving(true);
    try {
      const validation = clientProfileFormSchema.safeParse(formState);
      if (!validation.success) {
        throw new UserFacingError(validation.error.issues[0]?.message ?? 'Please correct the profile fields.');
      }
      const patched = clientProfileFormToPatientPatch(profileData.patient, formState);
      await medplum.updateResource<Patient>(patched);
      setHasChanges(false);
      notify({ type: 'success', title: 'Saved', message: 'Profile updated successfully.' });
      await profileData.refresh();
    } catch (err) {
      notify({
        type: 'error',
        title: 'Error',
        error: err,
        message: 'Failed to save profile.',
      });
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (!id) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red" variant="light">
          No client ID provided
        </Alert>
      </Container>
    );
  }

  if (profileData.loading && !profileData.patient) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center" py="xl">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading client profile…</Text>
        </Group>
      </Container>
    );
  }

  if (profileData.error && !profileData.patient) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red" variant="light">
          Failed to load client profile. {getUserFacingErrorMessage(profileData.error)}
        </Alert>
      </Container>
    );
  }

  // ---------------------------------------------------------------------------
  // Header data (prefer Patient in live mode)
  // ---------------------------------------------------------------------------

  const pt = profileData.patient;
  const displayName = pt ? getPatientPreferredName(pt) : (client ? `${client.preferredFirstName || client.firstName} ${client.preferredLastName || client.lastName}` : '');
  const officialName = pt ? getPatientOfficialName(pt) : (client ? `${client.firstName} ${client.lastName}` : '');
  const initials = pt ? getPatientInitials(pt) : (client ? [client.preferredFirstName || client.firstName, client.preferredLastName || client.lastName].map((n) => n?.charAt(0) ?? '').join('').toUpperCase() || client.name.charAt(0).toUpperCase() : '?');
  const emailDisplay = pt ? getPatientEmail(pt) : (client?.email ?? '');
  const statusLabel = pt ? (pt.active !== false ? 'Active' : 'Inactive') : (client?.status ?? 'Active');
  const statusColor = statusLabel === 'Active' ? 'green' : statusLabel === 'Inactive' ? 'gray' : 'yellow';

  const practiceName = profileData.managingOrganization?.name ?? client?.practice ?? '—';
  const providerName = profileData.activeEpisode?.careManager?.display ?? client?.primaryProvider ?? client?.provider ?? '—';
  const firstSession = profileData.appointments.first?.start
    ? formatDate(profileData.appointments.first.start)
    : (client?.firstSession ?? '—');
  const lastAppointment = profileData.appointments.last?.start
    ? formatDate(profileData.appointments.last.start)
    : (client?.mostRecentAppointment ?? '—');

  const breadcrumbItems = [
    { title: 'Caseload', href: '/dashboard/caseload' },
    { title: displayName || 'Client', href: `/dashboard/clients/${id}` },
    { title: 'Edit', href: '' },
  ].map((item, index) =>
    item.href ? (
      <Text key={index} component={Link} to={item.href} c="gold.7" size="sm" fw={500}>
        {item.title}
      </Text>
    ) : (
      <Text key={index} size="sm" c="gray.6">
        {item.title}
      </Text>
    ),
  );

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group gap="xs" align="center">
          <ActionIcon
            variant="subtle"
            color="gold.7"
            size="lg"
            onClick={() => navigate(`/dashboard/clients/${id}`)}
            aria-label="Go back"
          >
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        </Group>

        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Client Profile</Title>
            <Text c="gray.6" size="sm" mt="xs">
              Manage client details, billing, and contacts
            </Text>
          </div>
        </Group>

        {/* Profile Card (Part C — Header card) */}
        <Card withBorder>
          <Group gap="lg" align="flex-start">
            <Avatar size="xl" radius="xl" color="orange">
              {initials}
            </Avatar>
            <Stack gap="md" style={{ flex: 1 }}>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Title order={3}>
                    <Anchor component={Link} to={`/dashboard/clients/${id}`} c="gold.7" inherit>
                      {displayName}
                    </Anchor>
                  </Title>
                  <Text c="gray.6" size="sm" mt="xs">
                    {emailDisplay || 'No email on file'}
                  </Text>
                  {displayName !== officialName && (
                    <Text c="gray.6" size="xs" mt={2}>
                      Legal: {officialName}
                    </Text>
                  )}
                </div>
                <Badge color={statusColor} size="lg" variant="light">
                  {statusLabel}
                </Badge>
              </Group>

              <Grid gutter="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconBuilding size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">Practice:</Text>
                    <Text size="sm" fw={500}>{practiceName}</Text>
                  </Group>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconCalendar size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">First Session:</Text>
                    <Text size="sm" fw={500}>{firstSession}</Text>
                  </Group>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconActivity size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">Last Appointment:</Text>
                    <Text size="sm" fw={500}>{lastAppointment}</Text>
                  </Group>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconShield size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">Provider:</Text>
                    <Text size="sm" fw={500}>{providerName}</Text>
                  </Group>
                </Grid.Col>
              </Grid>
            </Stack>
          </Group>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
              Profile
            </Tabs.Tab>
            {visibleTabs.includes('billing') && (
              <Tabs.Tab value="billing" leftSection={<IconCreditCard size={16} />}>
                Billing
              </Tabs.Tab>
            )}
            {visibleTabs.includes('contacts') && (
              <Tabs.Tab value="contacts" leftSection={<IconUsers size={16} />}>
                Contacts
              </Tabs.Tab>
            )}
          </Tabs.List>

          {/* Profile Tab */}
          <Tabs.Panel value="profile" pt="lg">
            <Paper p={32} withBorder radius="md" shadow="sm">
              <Stack gap={24}>
                <Group grow>
                  <TextInput
                    label="Preferred First Name"
                    placeholder="Preferred First Name"
                    value={formState.preferredFirstName}
                    onChange={(e) => updateField('preferredFirstName', e.currentTarget.value)}
                  />
                  <TextInput
                    label="Preferred Last Name"
                    placeholder="Preferred Last Name"
                    value={formState.preferredLastName}
                    onChange={(e) => updateField('preferredLastName', e.currentTarget.value)}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="Legal First Name"
                    placeholder="Legal First Name"
                    value={formState.legalFirstName}
                    onChange={(e) => updateField('legalFirstName', e.currentTarget.value)}
                  />
                  <TextInput
                    label="Legal Last Name"
                    placeholder="Legal Last Name"
                    value={formState.legalLastName}
                    onChange={(e) => updateField('legalLastName', e.currentTarget.value)}
                  />
                </Group>
                <Group grow>
                  <UnifyDateInput
                    label="Date of Birth"
                    placeholder="Select a date"
                    value={formState.dateOfBirth || null}
                    onChange={(v) => updateField('dateOfBirth', v ?? '')}
                    leftSection={<IconCalendarEvent size={16} />}
                    clearable
                  />
                  <TextInput
                    label="Street Address"
                    placeholder="123 Main St"
                    value={formState.street}
                    onChange={(e) => updateField('street', e.currentTarget.value)}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="City"
                    placeholder="City"
                    value={formState.city}
                    onChange={(e) => updateField('city', e.currentTarget.value)}
                  />
                  <UsStateSelect
                    label="State"
                    placeholder="Select state"
                    value={formState.state}
                    onChange={(value) => updateField('state', value ?? '')}
                    clearable
                  />
                  <TextInput
                    label="ZIP Code"
                    placeholder="12345"
                    value={formState.zipCode}
                    onChange={(e) => updateField('zipCode', e.currentTarget.value)}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="Phone"
                    placeholder="(555) 000-0000"
                    value={formState.phone}
                    onChange={(e) => updateField('phone', e.currentTarget.value)}
                  />
                  <TextInput
                    label="Email"
                    placeholder="email@example.com"
                    value={formState.email}
                    onChange={(e) => updateField('email', e.currentTarget.value)}
                  />
                </Group>
                <Divider color="gray.3" />
                <div>
                  <Title order={5} mb="xs" c="dark">Gender Identity</Title>
                  <Radio.Group
                    value={formState.genderIdentity}
                    onChange={(v) => updateField('genderIdentity', v)}
                  >
                    <Group gap="lg">
                      {GENDER_IDENTITY_OPTIONS.map((opt) => (
                        <Radio key={opt} value={opt} label={opt} />
                      ))}
                    </Group>
                  </Radio.Group>
                </div>
                <Divider color="gray.3" />
                <ProfileCheckboxSection
                  label="LGBTQIA+ Identities"
                  options={[...LGBTQIA_OPTIONS]}
                  columns={3}
                  value={formState.lgbtqia}
                  onChange={(v) => updateField('lgbtqia', v)}
                />
                <Divider color="gray.3" />
                <ProfileCheckboxSection
                  label="Religion and Spirituality"
                  options={[...RELIGION_OPTIONS]}
                  columns={3}
                  value={formState.religion}
                  onChange={(v) => updateField('religion', v)}
                />
                <Divider color="gray.3" />
                <ProfileCheckboxSection
                  label="Race and Ethnicity"
                  options={[...RACE_ETHNICITY_OPTIONS]}
                  columns={3}
                  value={formState.raceEthnicity}
                  onChange={(v) => updateField('raceEthnicity', v)}
                />
                <Divider color="gray.3" />
                <ProfileCheckboxSection
                  label="Languages Spoken"
                  options={[...LANGUAGES_OPTIONS]}
                  columns={3}
                  value={formState.languages}
                  onChange={(v) => updateField('languages', v)}
                />
                <Divider color="gray.3" />
                <Group justify="flex-end" gap="md">
                  <Button
                    leftSection={<IconDeviceFloppy size={18} />}
                    onClick={handleSaveProfile}
                    disabled={!hasChanges || saving}
                    loading={saving}
                  >
                    Save Changes
                  </Button>
                </Group>
              </Stack>
            </Paper>

            {profileData.patient?.id && (
              <ClientAiDocumentationStatusCard patientId={profileData.patient.id} />
            )}
          </Tabs.Panel>

          {/* Billing Tab (Part F) */}
          {visibleTabs.includes('billing') && (
            <Tabs.Panel value="billing" pt="lg">
              <BillingTab
                patientId={patientId}
                patientDisplay={profileData.patient ? getPatientOfficialName(profileData.patient) : undefined}
                paymentMethods={profileData.billing.paymentMethods}
                coverages={profileData.billing.coverages}
                eligibilityResponses={profileData.billing.eligibilityResponses}
                encounters={profileData.billing.encounters}
                chargeItems={profileData.billing.chargeItems}
                invoices={profileData.billing.invoices}
                insuranceCards={profileData.billing.insuranceCards}
                authorizations={profileData.billing.authorizations}
                statements={profileData.billing.statements}
                relatedPersons={profileData.relatedPersons}
                loading={profileData.loading}
                onRefresh={profileData.refresh}
              />
            </Tabs.Panel>
          )}

          {/* Contacts Tab (Part G) */}
          {visibleTabs.includes('contacts') && (
            <Tabs.Panel value="contacts" pt="lg">
              <ContactsTab
                patient={profileData.patient}
                contacts={profileData.contacts}
                loading={profileData.loading}
                onRefresh={profileData.refresh}
              />
            </Tabs.Panel>
          )}
        </Tabs>
      </Stack>
    </Container>
  );
}
